<template>
    <div class="uploads-group">
        <div v-if="status === 'empty' || status === 'uploading'" class="card column uploads uploads--min-height-250 br c-lightest-bg text-center p-l-0 p-r-0 uploads-r-16_9">
            <div v-show="status === 'empty'" class="upload-content row row-middle full-width-dropzone">
                <drop-target
                    ref="dropTarget"
                    v-bind:id="fieldName"
                    v-bind:max-files="1"
                    v-bind:accepted-file-types="allowedExtensions"
                    v-bind:max-filesize="maxFilesizeMb"
                    context="audio"
                    v-bind:skip-commit="true"
                    v-on:upload:added="handleUploadAdded"
                    v-on:upload:progress="handleUploadProgress"
                    v-on:upload:success="handleUploadSuccess"
                    v-on:upload:error="handleUploadError"
                ></drop-target>
            </div>
            <div v-show="status === 'uploading'" class="upload-content row row-middle row-collapse">
                <div class="column text-center">
                    <div class="card-figure m-b-4">
                        <upload-progress-donut size="large" v-bind:percentage="uploadProgress"></upload-progress-donut>
                    </div>
                    <div class="card-content">
                        <h4 class="f-w-bold f-s-3 m-b-0">{{ trans('Uploading :fileName', {fileName: uploadFileName}) }}</h4>
                        <p class="f-s-3 m-0 c-medium-tint">{{ trans(':sentMB of :totalMB', {sent: uploadMbSent, total: uploadMbTotal}) }}</p>
                    </div>
                </div>
            </div>
            <div v-if="errorMessage" class="form-help c-error p-l-3 p-r-3">{{ errorMessage }}</div>
        </div>

        <div v-else class="slat br m-b-4 row row-middle row-collapse">
            <div class="column p-l-3 p-t-2 p-b-2">
                <div v-if="status === 'uploaded'" class="row row-middle row-collapse">
                    <!-- /icons/check.svg -->
                    <svg class="icon m-r-2 i-size-small" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g fill="#24D7C5"><path d="M19 4H5c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h14c.6 0 1-.4 1-1V5c0-.6-.4-1-1-1zm-1 2v9h-3c-.6 0-1 .4-1 1v1h-4v-1c0-.6-.4-1-1-1H6V6h12z"/><path d="M10.3 13.7c.5.5 1 .4 1.4 0l4-4c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0L11 11.6l-1.3-1.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l2 2z"/></g></svg>
                    <h4 class="f-w-bold f-s-3 m-b-0">{{ audioName }}</h4>
                    <audio v-if="localPreviewUrl" v-bind:src="localPreviewUrl" controls class="m-t-1 m-b-1" style="width: 100%;"></audio>
                    <p class="f-s-2 m-0 c-medium-tint">{{ trans('Uploaded. Audio will be processed after you save this lesson.') }}</p>
                </div>

                <div v-if="status === 'processing'" class="row row-middle row-collapse">
                    <div class="chartdonut chartdonut_animation--rotate chartdonut--small m-r-2">
                        <svg width="33" height="33"><g><circle cx="50%" cy="50%" r="13" stroke-width="3" stroke="#02DCAA" fill="none" stroke-dashoffset="5"></circle></g></svg>
                    </div>
                    <div>
                        <h4 class="f-w-bold f-s-3 m-b-0">{{ trans('Processing :fileName', {fileName: audioName}) }}</h4>
                    </div>
                </div>

                <div v-if="status === 'ready'">
                    <h4 class="f-w-bold f-s-3 m-b-1">{{ audioName }}</h4>
                    <audio v-if="previewUrl" v-bind:src="previewUrl" controls style="width: 100%;"></audio>
                </div>

                <div v-if="status === 'failed'">
                    <h4 class="f-w-bold f-s-3 m-b-0 c-error">{{ trans('Oops! Upload failed') }}</h4>
                    <p class="f-s-3 m-0 c-medium-tint">
                        {{ failureMessage }}
                        <a v-on:click.prevent="removeAudio" class="ul" href="#try-again">{{ trans('Try Again.') }}</a>
                    </p>
                </div>
            </div>
            <div class="text-right p-l-3 p-r-3">
                <a class="i-container i-filled-dark cu-p" href="#remove-audio" v-on:click.prevent="removeAudio">
                    <!-- /icons/remove.svg -->
                    <svg class="icon icon-remove i-size-medium" width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><circle fill="#FFF" cx="16" cy="16" r="16"/><path fill="#AAB2BD" fill-rule="nonzero" d="M23 10.41L21.59 9 16 14.59 10.41 9 9 10.41 14.59 16 9 21.59 10.41 23 16 17.41 21.59 23 23 21.59 17.41 16z"/></g></svg>
                </a>
            </div>
        </div>

        <div v-if="hasAudio" class="form-group m-b-2">
            <label class="f-s-2 f-w-bold m-b-1">{{ trans('Audio title') }}</label>
            <input type="text" class="input__field" v-model="audioTitle" v-bind:placeholder="trans('Audio title')">
        </div>

        <toggle
            v-if="hasAudio"
            v-bind:name="fieldName + '_allow_download'"
            v-bind:label="trans('Allow members to download this audio file')"
            v-bind:toggled.sync="allowDownloadValue"
            class-name="form-group-addon m-b-2"
        ></toggle>

        <input type="hidden" v-bind:name="fieldName + '_path'" v-bind:value="audioPath || ''">
        <input type="hidden" v-bind:name="fieldName + '_name'" v-bind:value="audioName || ''">
        <input type="hidden" v-bind:name="fieldName + '_title'" v-bind:value="audioTitle || ''">
        <input type="hidden" v-bind:name="fieldName + '_remove'" v-bind:value="audioRemove ? '1' : ''">
    </div>
</template>
<script>
import { AUDIO_STATUS_READY, AUDIO_STATUS_FAILED } from '../../constants';
import { EventBus } from '../../event-bus.js';
import { audioStatusUrl, audioPreviewUrl } from '../../routes.js';
import DropTarget from './DropTarget.vue';

export default {
    components: {
        DropTarget,
    },
    props: {
        fieldName: {
            type: String,
            default: 'audio',
        },
        existingAudioPath: {
            type: String,
            default: null,
        },
        existingAudioName: {
            type: String,
            default: null,
        },
        existingAudioTitle: {
            type: String,
            default: null,
        },
        existingAudioStatus: {
            type: Number,
            default: null,
        },
        existingAudioDuration: {
            type: Number,
            default: null,
        },
        existingFailureCode: {
            type: String,
            default: null,
        },
        audioId: {
            type: String,
            default: '',
        },
        lessonId: {
            type: String,
            default: null,
        },
        allowDownload: {
            type: Boolean,
            default: false,
        },
        allowedExtensions: {
            type: String,
            default: '.mp3,.m4a,.aac,.wav,.flac,.ogg,.oga,.aiff,.aif',
        },
        maxFilesizeMb: {
            type: Number,
            default: 100,
        },
    },
    data() {
        return {
            status: 'empty',

            uploadId: null,
            uploadProgress: 0,
            uploadFileName: null,
            uploadMbSent: 0,
            uploadMbTotal: 0,
            errorMessage: null,

            audioPath: this.existingAudioPath,
            audioName: this.existingAudioName,
            audioTitle: this.existingAudioTitle,
            audioRemove: false,
            audioDuration: this.existingAudioDuration,
            failureCode: this.existingFailureCode,
            allowDownloadValue: this.allowDownload,

            previewUrl: null,
            localPreviewUrl: null,
            statusTimeout: null,
            currentDelay: 10000,
        };
    },
    computed: {
        hasAudio() {
            return this.status === 'uploaded'
                || this.status === 'processing'
                || this.status === 'ready'
                || this.status === 'failed';
        },
        humanDuration() {
            if ( ! this.audioDuration) {
                return null;
            }

            const minutes = Math.floor(this.audioDuration / 60);
            const seconds = Math.floor(this.audioDuration % 60).toString().padStart(2, '0');

            return minutes + ':' + seconds;
        },
        failureMessage() {
            switch (this.failureCode) {
                case 'no_audio_stream':
                    return this.trans('File has no audio track.');
                case 'file_too_large':
                    return this.trans('File is larger than :maxSizeMb MB — try uploading an MP3 instead of WAV or FLAC.', {maxSizeMb: this.maxFilesizeMb});
                case 'probe_failed':
                    return this.trans("We couldn't read this file.");
                case 'unsupported_container':
                    return this.trans("This file's format isn't supported.");
                case 'transcode_failed':
                    return this.trans('Converting this file failed.');
                default:
                    return this.trans('Something went wrong.');
            }
        },
    },
    watch: {
        audioPath(newValue) {
            EventBus.$emit('form:element:changed', this.fieldName + '_path', newValue || '');
        },
        audioTitle(newValue) {
            EventBus.$emit('form:element:changed', this.fieldName + '_title', newValue || '');
        },
    },
    methods: {
        stripExtension(name) {
            if ( ! name) {
                return null;
            }

            const lastDot = name.lastIndexOf('.');

            return lastDot > 0 ? name.substring(0, lastDot) : name;
        },
        handleUploadAdded({uploadId, file}) {
            this.uploadId = uploadId;
            this.status = 'uploading';
            this.errorMessage = null;
            this.audioRemove = false;
            this.uploadFileName = file.name;
            this.uploadProgress = 0;
            this.uploadMbSent = 0;
            this.uploadMbTotal = (file.size / 1024 / 1024).toFixed(2);
            this.audioTitle = this.stripExtension(file.name);

            EventBus.$emit('form:save:unsafe');
        },
        handleUploadProgress({sent, total}) {
            this.uploadProgress = total ? parseInt((sent / total) * 100) : 0;
            this.uploadMbSent = (sent / 1024 / 1024).toFixed(2);
        },
        handleUploadSuccess({file, committed}) {
            this.audioPath = committed.path;
            this.audioName = committed.name;
            this.status = 'uploaded';

            // Preview the browser's own copy of the file immediately —
            // no need to wait for the server-side transcode to finish
            // just to let the creator sanity-check what they uploaded.
            this.revokeLocalPreviewUrl();
            this.localPreviewUrl = URL.createObjectURL(file);

            EventBus.$emit('form:save:safe');
        },
        handleUploadError({message}) {
            this.errorMessage = message || this.trans('Something went wrong.');
            this.status = 'empty';

            EventBus.$emit('form:save:safe');
        },
        removeAudio() {
            const hadReadyAudio = this.status === 'ready';

            if (this.status === 'uploading' && this.uploadId && this.$refs.dropTarget) {
                this.$refs.dropTarget.cancelUpload(this.uploadId);
            } else if (this.status === 'uploaded' && this.audioPath) {
                // Already uploaded but not yet saved — the tmp object is
                // ours to clean up immediately rather than waiting on the
                // lifecycle rule.
                window.axios.post(window.Kourses.activeBaseUrl + '/upload/abandon', {key: this.audioPath});
            }

            this.clearStatusTimeout();

            this.status = 'empty';
            this.uploadId = null;
            this.audioPath = '';
            this.audioRemove = true;
            this.audioName = null;
            this.audioTitle = null;
            this.audioDuration = null;
            this.failureCode = null;
            this.errorMessage = null;
            this.previewUrl = null;
            this.revokeLocalPreviewUrl();

            if (this.$refs.dropTarget) {
                this.$refs.dropTarget.reset();
            }

            if (hadReadyAudio) {
                EventBus.$emit(this.fieldName + ':audio:exists', false);
            }

            EventBus.$emit('form:save:safe');
        },
        clearStatusTimeout() {
            if (this.statusTimeout) {
                clearTimeout(this.statusTimeout);
                this.statusTimeout = null;
            }
        },
        revokeLocalPreviewUrl() {
            if (this.localPreviewUrl) {
                URL.revokeObjectURL(this.localPreviewUrl);
                this.localPreviewUrl = null;
            }
        },
        pollStatus() {
            this.clearStatusTimeout();

            window.axios.get(audioStatusUrl(this.audioId))
                .then(({data}) => {
                    if (data.status === AUDIO_STATUS_READY) {
                        this.status = 'ready';
                        this.audioDuration = data.duration;
                        this.fetchPreviewUrl();

                        EventBus.$emit(this.fieldName + ':audio:ready');

                        return;
                    }

                    if (data.status === AUDIO_STATUS_FAILED) {
                        this.status = 'failed';
                        this.failureCode = data.failure_code;

                        return;
                    }

                    this.currentDelay = Math.min(this.currentDelay * 1.5, 30000);
                    this.statusTimeout = setTimeout(() => this.pollStatus(), this.currentDelay);
                })
                .catch(() => {
                    this.statusTimeout = setTimeout(() => this.pollStatus(), this.currentDelay);
                });
        },
        fetchPreviewUrl() {
            window.axios.get(audioPreviewUrl(this.audioId))
                .then(({data}) => {
                    this.previewUrl = data.url;
                });
        },
    },
    mounted() {
        if ( ! this.audioId) {
            // No saved audio row yet — either a fresh field, or a failed
            // form validation resubmit that still carries a pending tmp
            // upload via old('audio_path').
            this.status = this.audioPath && this.audioPath.startsWith('tmp/') ? 'uploaded' : 'empty';

            return;
        }

        if (this.existingAudioStatus === AUDIO_STATUS_READY) {
            this.status = 'ready';
            this.fetchPreviewUrl();
        } else if (this.existingAudioStatus === AUDIO_STATUS_FAILED) {
            this.status = 'failed';
        } else {
            this.status = 'processing';
            this.pollStatus();
        }
    },
    beforeDestroy() {
        this.clearStatusTimeout();
        this.revokeLocalPreviewUrl();
    },
}
</script>
